import { useEffect, useMemo, useState } from "react";
import { Head } from 'vite-react-ssg';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowDown, ArrowUp, Minus, Loader2, RefreshCw } from "lucide-react";

type Row = {
  week_start: string;
  type: "query" | "page";
  key: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

type ReportData = { weeks: string[]; rows: Row[] };

const STORAGE_KEY = "seo_report_pass";

const fmtPct = (n: number) => `${(n * 100).toFixed(2)}%`;
const fmtPos = (n: number) => n.toFixed(2);
const fmtInt = (n: number) => n.toLocaleString();

function Delta({ value, suffix = "", invert = false }: { value: number; suffix?: string; invert?: boolean }) {
  if (!isFinite(value) || Math.abs(value) < 0.0001) {
    return <span className="inline-flex items-center text-muted-foreground text-xs"><Minus className="h-3 w-3" /></span>;
  }
  const positive = invert ? value < 0 : value > 0;
  const Icon = value > 0 ? ArrowUp : ArrowDown;
  const cls = positive ? "text-green-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${cls}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}{suffix}
    </span>
  );
}

export default function SeoReport() {
  const [pass, setPass] = useState("");
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (passphrase: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke("seo-report-read", {
        body: { passphrase },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Failed");
      setData(data as ReportData);
      localStorage.setItem(STORAGE_KEY, passphrase);
    } catch (e: any) {
      setError(e?.message || "Failed to load report");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) { setPass(saved); load(saved); }
  }, []);

  const view = useMemo(() => {
    if (!data || data.weeks.length === 0) return null;
    const [latest, prev] = data.weeks;
    const split = (type: "query" | "page") => {
      const cur = data.rows.filter(r => r.type === type && r.week_start === latest);
      const pre = data.rows.filter(r => r.type === type && r.week_start === prev);
      const preMap = new Map(pre.map(r => [r.key, r]));
      return cur
        .map(r => ({ ...r, prev: preMap.get(r.key) }))
        .sort((a, b) => b.clicks - a.clicks);
    };
    const queries = split("query");
    const pages = split("page");

    const totals = (rows: typeof queries) => ({
      clicks: rows.reduce((s, r) => s + r.clicks, 0),
      impressions: rows.reduce((s, r) => s + r.impressions, 0),
    });
    const cur = totals(queries);
    const preRows = data.rows.filter(r => r.type === "query" && r.week_start === prev);
    const pre = totals(preRows as any);

    // Movers: biggest clicks delta among queries that existed in both weeks
    const movers = queries
      .filter(r => r.prev)
      .map(r => ({ ...r, delta: r.clicks - (r.prev?.clicks ?? 0) }))
      .filter(r => Math.abs(r.delta) >= 5)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 20);

    return { latest, prev, queries, pages, cur, pre, movers };
  }, [data]);

  if (!data) {
    return (
      <>
        <Head>
          <title>SEO Report</title>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 space-y-4">
            <h1 className="text-xl font-semibold">SEO Report</h1>
            <p className="text-sm text-muted-foreground">Enter the passphrase to view weekly Search Console data.</p>
            <Input
              type="password"
              value={pass}
              onChange={e => setPass(e.target.value)}
              placeholder="Passphrase"
              onKeyDown={e => e.key === "Enter" && pass && load(pass)}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button onClick={() => load(pass)} disabled={!pass || loading} className="w-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Unlock
            </Button>
          </Card>
        </div>
      </>
    );
  }

  if (!view) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 max-w-md text-center space-y-3">
          <p className="text-sm text-muted-foreground">No snapshots yet. The first one runs every Monday at 09:00 UTC.</p>
          <Button variant="outline" onClick={() => { localStorage.removeItem(STORAGE_KEY); setData(null); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Reset
          </Button>
        </Card>
      </div>
    );
  }

  const clicksDelta = view.cur.clicks - view.pre.clicks;
  const imprDelta = view.cur.impressions - view.pre.impressions;

  const renderRows = (rows: any[]) => rows.slice(0, 50).map((r) => {
    const dClicks = r.prev ? r.clicks - r.prev.clicks : 0;
    const dPos = r.prev ? r.position - r.prev.position : 0;
    return (
      <TableRow key={`${r.type}-${r.key}`}>
        <TableCell className="font-medium max-w-md truncate">{r.key}</TableCell>
        <TableCell className="text-right">{fmtInt(r.clicks)}</TableCell>
        <TableCell className="text-right"><Delta value={dClicks} /></TableCell>
        <TableCell className="text-right">{fmtInt(r.impressions)}</TableCell>
        <TableCell className="text-right">{fmtPct(r.ctr)}</TableCell>
        <TableCell className="text-right">{fmtPos(r.position)}</TableCell>
        <TableCell className="text-right"><Delta value={dPos} invert /></TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <Head>
        <title>SEO Report</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <div className="container mx-auto py-8 px-4 space-y-6 max-w-7xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">SEO Report</h1>
            <p className="text-sm text-muted-foreground">
              Week of {view.latest}{view.prev ? ` (vs ${view.prev})` : ""}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(pass)} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Total clicks</p>
            <p className="text-2xl font-semibold">{fmtInt(view.cur.clicks)}</p>
            <Delta value={clicksDelta} />
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Total impressions</p>
            <p className="text-2xl font-semibold">{fmtInt(view.cur.impressions)}</p>
            <Delta value={imprDelta} />
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Avg CTR</p>
            <p className="text-2xl font-semibold">
              {view.cur.impressions ? fmtPct(view.cur.clicks / view.cur.impressions) : "—"}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Tracked queries</p>
            <p className="text-2xl font-semibold">{view.queries.length}</p>
          </Card>
        </div>

        <Tabs defaultValue="queries">
          <TabsList>
            <TabsTrigger value="queries">Top queries</TabsTrigger>
            <TabsTrigger value="pages">Top pages</TabsTrigger>
            <TabsTrigger value="movers">Movers</TabsTrigger>
          </TabsList>

          <TabsContent value="queries">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Query</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Δ</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">Pos</TableHead>
                    <TableHead className="text-right">Δ pos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{renderRows(view.queries)}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="pages">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Δ</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">Pos</TableHead>
                    <TableHead className="text-right">Δ pos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{renderRows(view.pages)}</TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="movers">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Query</TableHead>
                    <TableHead className="text-right">Clicks now</TableHead>
                    <TableHead className="text-right">Clicks before</TableHead>
                    <TableHead className="text-right">Δ Clicks</TableHead>
                    <TableHead className="text-right">Pos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {view.movers.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Need at least 2 weeks of data to show movers.</TableCell></TableRow>
                  ) : view.movers.map((r) => (
                    <TableRow key={`mover-${r.key}`}>
                      <TableCell className="font-medium max-w-md truncate">{r.key}</TableCell>
                      <TableCell className="text-right">{fmtInt(r.clicks)}</TableCell>
                      <TableCell className="text-right">{fmtInt(r.prev?.clicks ?? 0)}</TableCell>
                      <TableCell className="text-right"><Delta value={r.delta} /></TableCell>
                      <TableCell className="text-right">{fmtPos(r.position)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-xs text-muted-foreground pt-4 border-t">
          Snapshots run every Monday at 09:00 UTC from Google Search Console. Bookmark this page.{" "}
          <button onClick={() => { localStorage.removeItem(STORAGE_KEY); setData(null); }} className="underline">Lock</button>
        </div>
      </div>
    </>
  );
}
